import React, { useState, useMemo, useEffect } from 'react';
import { FaCaretDown, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { Ship } from '../../interfaces/types';
import { TYPE_TRANSLATIONS, SIZE_TRANSLATIONS, PRODUCTION_STATUS_TRANSLATIONS } from '../../utils/translations';
import './FiltersMenu.css';

export const SORT_OPTIONS = {
  'name': 'Nombre',
  'size': 'Tamaño',
  'cargo': 'Capacidad de carga'
} as const;

export interface SortConfig {
  field: 'name' | 'size' | 'cargo';
  direction: 'asc' | 'desc';
}

interface FiltersMenuProps {
  showFilters: boolean;
  handleFiltersToggle: () => void;
  isGrouped: boolean;
  setIsGrouped: (grouped: boolean) => void;
  sortConfig: SortConfig;
  setSortConfig: (config: SortConfig) => void;
  showUsersFilter: boolean;
  showUserMenu: boolean;
  toggleUserMenu: () => void;
  selectedUser: string;
  setSelectedUser: (user: string) => void;
  usersList: string[];
  filteredShips: Ship[];
  showTypeMenu: boolean;
  toggleTypeMenu: () => void;
  selectedType: string;
  setSelectedType: (type: string) => void;
  shipTypes: string[];
  shipsByType: Record<string, Ship[]>;
  showSizeMenu: boolean;
  toggleSizeMenu: () => void;
  selectedSize: string;
  setSelectedSize: (size: string) => void;
  shipsBySize: Record<string, Ship[]>;
  showFocusMenu: boolean;
  toggleFocusMenu: () => void;
  selectedFocus: string;
  setSelectedFocus: (focus: string) => void;
  showProductionStatusMenu: boolean;
  toggleProductionStatusMenu: () => void;
  selectedProductionStatus: string;
  setSelectedProductionStatus: (status: string) => void;
  rawShips: Ship[];
  username: string;
  isAdmin?: boolean;
}

interface FocusPaginatedSectionProps {
  rawShips: Ship[];
  filteredShips: Ship[];
  selectedFocus: string;
  setSelectedFocus: (focus: string) => void;
}

interface UsersPaginatedSectionProps {
  usersList: string[];
  filteredShips: Ship[];
  rawShips: Ship[];
  selectedUser: string;
  setSelectedUser: (user: string) => void;
  username: string;
  isAdmin: boolean;
}

const FocusPaginatedSection: React.FC<FocusPaginatedSectionProps> = ({
  rawShips,
  filteredShips,
  selectedFocus,
  setSelectedFocus
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const uniqueFocuses = useMemo(() => {
    return Array.from(new Set(rawShips?.map(ship => ship.focus)));
  }, [rawShips]);

  const totalPages = Math.ceil(uniqueFocuses.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const currentFocuses = uniqueFocuses.slice(startIndex, startIndex + itemsPerPage);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <>
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            className="pagination-arrow" 
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            title="Página anterior"
          >
            <FaChevronUp />
          </button>
          <span className="pagination-info">
            {currentPage + 1}/{totalPages}
          </span>
          <button 
            className="pagination-arrow" 
            onClick={handleNextPage}
            disabled={currentPage === totalPages - 1}
            title="Página siguiente"
          >
            <FaChevronDown />
          </button>
        </div>
      )}
      <div className="filter-buttons">
        <button
          className={`filter-button ${selectedFocus === '' ? 'active' : ''}`}
          onClick={() => setSelectedFocus('')}
        >
          Todos
          <span className="count">
            {rawShips?.length || 0}
          </span>
        </button>
        {currentFocuses.map(focus => (
          <button
            key={focus}
            className={`filter-button ${selectedFocus === focus ? 'active' : ''}`}
            onClick={() => setSelectedFocus(focus)}
          >
            {focus || 'Sin enfoque'}
            <span className="count">
              {filteredShips.filter(ship => ship.focus === focus).length || 0}
            </span>
          </button>
        ))}
      </div>
    </>
  );
};

const USERS_PER_PAGE = 10;

const UsersFilterSection: React.FC<UsersPaginatedSectionProps> = ({
  usersList,
  rawShips,
  selectedUser,
  setSelectedUser,
  username,
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  const otherUsers = useMemo(() => {
    if (!username) return usersList;
    return usersList.filter((user) => user !== username);
  }, [usersList, username]);

  const totalPages = Math.max(1, Math.ceil(otherUsers.length / USERS_PER_PAGE));
  const startIndex = currentPage * USERS_PER_PAGE;
  const currentUsers = otherUsers.slice(startIndex, startIndex + USERS_PER_PAGE);

  const shipCountByUser = useMemo(() => {
    const counts = new Map<string, number>();
    (rawShips || []).forEach((ship) => {
      if (!ship.usuario) return;
      counts.set(ship.usuario, (counts.get(ship.usuario) || 0) + 1);
    });
    return counts;
  }, [rawShips]);

  useEffect(() => {
    setCurrentPage(0);
  }, [usersList.length, username]);

  useEffect(() => {
    if (currentPage > totalPages - 1) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!selectedUser || selectedUser === username) return;
    const index = otherUsers.indexOf(selectedUser);
    if (index < 0) return;
    setCurrentPage(Math.floor(index / USERS_PER_PAGE));
  }, [selectedUser, otherUsers, username]);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <>
      {otherUsers.length > USERS_PER_PAGE && (
        <div className="pagination-controls">
          <button
            type="button"
            className="pagination-arrow"
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            title="Página anterior"
          >
            <FaChevronUp />
          </button>
          <span className="pagination-info">
            {currentPage + 1}/{totalPages}
          </span>
          <button
            type="button"
            className="pagination-arrow"
            onClick={handleNextPage}
            disabled={currentPage >= totalPages - 1}
            title="Página siguiente"
          >
            <FaChevronDown />
          </button>
        </div>
      )}
      <div className="filter-buttons">
        <button
          type="button"
          className={`filter-button ${selectedUser === '' ? 'active' : ''}`}
          onClick={() => setSelectedUser('')}
        >
          Todos
          <span className="count">{rawShips?.length || 0}</span>
        </button>

        {username && (
          <button
            type="button"
            className={`filter-button ${selectedUser === username ? 'active' : ''}`}
            onClick={() => setSelectedUser(username)}
          >
            {username} (Tú)
            <span className="count">{shipCountByUser.get(username) || 0}</span>
          </button>
        )}

        {currentUsers.map((user: string) => (
          <button
            key={user}
            type="button"
            className={`filter-button ${selectedUser === user ? 'active' : ''}`}
            onClick={() => setSelectedUser(user)}
          >
            {user === 'anonymous' ? 'Anónimo' : user}
            <span className="count">{shipCountByUser.get(user) || 0}</span>
          </button>
        ))}
      </div>
    </>
  );
};

export const FiltersMenu: React.FC<FiltersMenuProps> = ({
  showFilters,
  handleFiltersToggle,
  isGrouped,
  setIsGrouped,
  sortConfig,
  setSortConfig,
  showUsersFilter,
  showUserMenu,
  toggleUserMenu,
  selectedUser,
  setSelectedUser,
  usersList,
  filteredShips,
  showTypeMenu,
  toggleTypeMenu,
  selectedType,
  setSelectedType,
  shipTypes,
  shipsByType,
  showSizeMenu,
  toggleSizeMenu,
  selectedSize,
  setSelectedSize,
  shipsBySize,
  showFocusMenu,
  toggleFocusMenu,
  selectedFocus,
  setSelectedFocus,
  showProductionStatusMenu,
  toggleProductionStatusMenu,
  selectedProductionStatus,
  setSelectedProductionStatus,
  rawShips,
  username,
  isAdmin = false
}) => {
  return (
    <aside className={`filters-nav ${!showFilters ? 'collapsed' : ''}`}>
      <button
        className="collapse-button right"
        onClick={handleFiltersToggle}
        title={showFilters ? "Ocultar filtros" : "Mostrar filtros"}
      />
      <div className="filters-content">

        <div className="filter-section">
          <h3>Ordenación</h3>
          <div className="filter-content">
            <select 
              className="filter-select"
              value={`${sortConfig.field}-${sortConfig.direction}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split('-');
                setSortConfig({ 
                  field: field as 'name' | 'size' | 'cargo', 
                  direction: direction as 'asc' | 'desc'
                });
              }}
            >
              {Object.entries(SORT_OPTIONS).map(([field, label]) => (
                <React.Fragment key={field}>
                  <option value={`${field}-asc`}>{label} (A-Z)</option>
                  <option value={`${field}-desc`}>{label} (Z-A)</option>
                </React.Fragment>
              ))}
            </select>
          </div>
        </div>

        <div className={`filter-section ${!showProductionStatusMenu ? 'collapsed' : ''}`}>
          <h3 onClick={toggleProductionStatusMenu}>
            Estado de Producción <FaCaretDown className={`caret ${!showProductionStatusMenu ? 'rotated' : ''}`} />
          </h3>
          <div className="filter-content">
            <div className="filter-buttons">
              <button
                className={`filter-button ${selectedProductionStatus === '' ? 'active' : ''}`}
                onClick={() => setSelectedProductionStatus('')}
              >
                Todos
                <span className="count">
                  {rawShips?.length || 0}
                </span>
              </button>
              {Object.entries(PRODUCTION_STATUS_TRANSLATIONS).map(([statusKey, statusLabel]) => (
                <button
                  key={statusKey}
                  className={`filter-button ${selectedProductionStatus === statusKey ? 'active' : ''}`}
                  onClick={() => setSelectedProductionStatus(statusKey)}
                >
                  {statusLabel}
                  <span className="count">
                    {rawShips?.filter(ship => 
                      (ship.production_status || 'undefined')?.toLowerCase() === statusKey.toLowerCase()
                    ).length || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {showUsersFilter && (
          <div className={`filter-section ${!showUserMenu ? 'collapsed' : ''}`}>
            <h3 onClick={toggleUserMenu}>
              Usuario <FaCaretDown className={`caret ${!showUserMenu ? 'rotated' : ''}`} />
            </h3>
            <div className="filter-content">
              <UsersFilterSection
                usersList={usersList}
                filteredShips={filteredShips}
                rawShips={rawShips}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                username={username}
                isAdmin={isAdmin}
              />
            </div>
          </div>
        )}

        <div className={`filter-section ${!showTypeMenu ? 'collapsed' : ''}`}>
          <h3 onClick={toggleTypeMenu}>
            Tipos <FaCaretDown className={`caret ${!showTypeMenu ? 'rotated' : ''}`} />
          </h3>
          <div className="filter-content">
            <div className="filter-buttons">
              <button
                className={`filter-button ${selectedType === '' ? 'active' : ''}`}
                onClick={() => setSelectedType('')}
              >
                Todos
                <span className="count">
                  {rawShips?.length || 0}
                </span>
              </button>
              {shipTypes?.map(type => {
                const typeKey = (type || 'undefined').toLowerCase();
                return (
                  <button
                    key={typeKey}
                    className={`filter-button ${selectedType === type ? 'active' : ''}`}
                    onClick={() => setSelectedType(type)}
                  >
                    {TYPE_TRANSLATIONS[typeKey] || type || 'Sin tipo'}
                    <span className="count">
                      {shipsByType[typeKey]?.length || 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className={`filter-section ${!showSizeMenu ? 'collapsed' : ''}`}>
          <h3 onClick={toggleSizeMenu}>
            Tamaños <FaCaretDown className={`caret ${!showSizeMenu ? 'rotated' : ''}`} />
          </h3>
          <div className="filter-content">
            <div className="filter-buttons">
              <button
                className={`filter-button ${selectedSize === '' ? 'active' : ''}`}
                onClick={() => setSelectedSize('')}
              >
                Todos
                <span className="count">
                  {rawShips?.length || 0}
                </span>
              </button>
              {Object.entries(SIZE_TRANSLATIONS).map(([englishKey, spanishLabel]) => (
                <button
                  key={englishKey}
                  className={`filter-button ${selectedSize === englishKey ? 'active' : ''}`}
                  onClick={() => setSelectedSize(englishKey)}
                >
                  {spanishLabel}
                  <span className="count">
                    {shipsBySize[englishKey.toLowerCase()]?.length || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={`filter-section ${!showFocusMenu ? 'collapsed' : ''}`}>
          <h3 onClick={toggleFocusMenu}>
            Enfoque <FaCaretDown className={`caret ${!showFocusMenu ? 'rotated' : ''}`} />
          </h3>
          <div className="filter-content">
            <FocusPaginatedSection 
              rawShips={rawShips}
              filteredShips={filteredShips}
              selectedFocus={selectedFocus}
              setSelectedFocus={setSelectedFocus}
            />
          </div>
        </div>
      </div>
    </aside>
  );
}; 